package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.model.NewReminder
import com.dotdrives.model.ReminderSubjectOption
import com.dotdrives.model.ReminderSummary
import com.dotdrives.repository.ReminderRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlin.time.Instant

data class NewReminderUiState(
    val description: String = "",
    val dueAt: Instant? = null,
    val recurrence: String? = null,
    val assignedToUserId: String? = null,
    val subjectQuery: String = "",
    val subjectResults: List<ReminderSubjectOption> = emptyList(),
    val selectedSubject: ReminderSubjectOption? = null,
    val alsoEmailMe: Boolean = true,
    val saving: Boolean = false,
    val savedReminder: ReminderSummary? = null,
    val error: String? = null,
)

class NewReminderController(
    private val repo: ReminderRepository,
    scope: CoroutineScope,
    private val orgId: String,
    private val defaultAssignedToUserId: String,
) : DotController<NewReminderUiState>(NewReminderUiState(assignedToUserId = defaultAssignedToUserId), scope) {
    private var searchJob: Job? = null
    private var generation = 0
    private var searchGeneration = 0

    fun start() {
        generation++
        searchGeneration++
        searchJob?.cancel()
        update { NewReminderUiState(assignedToUserId = defaultAssignedToUserId) }
    }

    fun setDescription(text: String) {
        update { it.copy(description = text, error = null) }
    }

    fun setDueAt(dueAt: Instant) {
        update { it.copy(dueAt = dueAt, error = null) }
    }

    fun setRecurrence(recurrence: String?) {
        update { it.copy(recurrence = recurrence) }
    }

    fun setAssignedToUserId(userId: String) {
        update { it.copy(assignedToUserId = userId) }
    }

    fun setAlsoEmailMe(enabled: Boolean) {
        update { it.copy(alsoEmailMe = enabled) }
    }

    fun searchSubject(query: String) {
        val request = ++searchGeneration
        searchJob?.cancel()
        update { it.copy(subjectQuery = query, error = null) }
        val trimmed = query.trim()
        if (trimmed.isEmpty()) {
            update { it.copy(subjectResults = emptyList()) }
            return
        }
        searchJob = scope.launch {
            delay(SEARCH_DEBOUNCE_MILLIS)
            repo.searchSubjects(orgId, trimmed)
                .onSuccess { results -> if (request == searchGeneration) update { it.copy(subjectResults = results) } }
                .onFailure { error -> if (request == searchGeneration) update { it.copy(error = error.message ?: "Something went wrong") } }
        }
    }

    fun selectSubject(option: ReminderSubjectOption) {
        searchGeneration++
        searchJob?.cancel()
        update { it.copy(selectedSubject = option, subjectQuery = option.label, subjectResults = emptyList(), error = null) }
    }

    fun save() {
        val current = state.value
        if (current.saving) return
        val subject = current.selectedSubject
        if (subject == null) {
            update { it.copy(error = "Link this step to a giver, drive, or report") }
            return
        }
        val dueAt = current.dueAt
        if (dueAt == null) {
            update { it.copy(error = "Choose a due date") }
            return
        }
        val assignedToUserId = current.assignedToUserId
        if (assignedToUserId == null) {
            update { it.copy(error = "Choose who this is for") }
            return
        }
        val description = current.description.trim()
        if (description.isEmpty()) {
            update { it.copy(error = "Say what needs to happen") }
            return
        }
        val request = generation
        val newReminder = NewReminder(
            description = description,
            subjectType = subject.subjectType,
            subjectId = subject.subjectId,
            assignedToUserId = assignedToUserId,
            dueAt = dueAt,
            recurrence = current.recurrence,
            emailEnabled = current.alsoEmailMe,
        )
        update { it.copy(saving = true, error = null, savedReminder = null) }
        scope.launch {
            repo.create(orgId, newReminder)
                .onSuccess { saved -> if (request == generation) update { it.copy(saving = false, savedReminder = saved, error = null) } }
                .onFailure { error -> if (request == generation) update { it.copy(saving = false, error = error.message ?: "Something went wrong") } }
        }
    }
}

private const val SEARCH_DEBOUNCE_MILLIS = 250L
